import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  Paper,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
} from '@mui/material';
import { CPT_CODES_FALLBACK } from '../../../constants/CPTCodes';
import { CPTCodesDropdownArray } from '../../CPTCodesDropdown';
import SDPServiceCodesDropdownArray from '../../SDPServiceCodesDropdown/SDPServiceCodesDropdownArray';
import IEPServiceTherapiesField from './IEPServiceTherapiesField';
import { fetchPatientTreatments } from '../../../api/patients';

const BillingInformationCard = ({
  isBillable,
  cptCodes,
  sdpServiceCodes,
  iepServiceTherapies,
  nonBillableReason,
  onFieldChange,
  subrole,
  studentId,
  typeOfUser,
}) => {
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch treatments when studentId changes
  useEffect(() => {
    if (!studentId || !typeOfUser) return;

    const loadTreatments = async () => {
      setLoading(true);
      try {
        const fetchedTreatments = await fetchPatientTreatments(studentId, typeOfUser);
        if (fetchedTreatments && Array.isArray(fetchedTreatments)) {
          setTreatments(fetchedTreatments);
        } else {
          setTreatments([]);
        }
      } catch (error) {
        console.error('Error fetching treatments:', error);
        setTreatments([]);
      } finally {
        setLoading(false);
      }
    };

    loadTreatments();
  }, [studentId, typeOfUser]);

  // Check if at least one therapy has SDP selected
  const hasSDPTherapy = useMemo(() => {
    if (!treatments || treatments.length === 0) return false;

    return treatments.some((treatment) => {
      if (treatment.therapies && Array.isArray(treatment.therapies)) {
        return treatment.therapies.some((therapy) => {
          return therapy.insuranceTypes?.includes('sdp');
        });
      }
      return false;
    });
  }, [treatments]);

  // Check if at least one therapy has IEP selected
  const hasIEPTherapy = useMemo(() => {
    if (!treatments || treatments.length === 0) return false;

    return treatments.some((treatment) => {
      if (treatment.therapies && Array.isArray(treatment.therapies)) {
        return treatment.therapies.some((therapy) => {
          return therapy.insuranceTypes?.includes('iep');
        });
      }
      return false;
    });
  }, [treatments]);

  // Get all codes for grouped display
  const allCptCodes = useMemo(() => {
    return CPT_CODES_FALLBACK;
  }, []);
  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography
        variant='h6'
        sx={{ mb: 2, color: '#9291f0', fontWeight: 600 }}
      >
        Billing Information
      </Typography>

      <FormControl component='fieldset' sx={{ mb: 2 }}>
        <FormLabel component='legend'>Is Billable?</FormLabel>
        <RadioGroup
          row
          value={isBillable ? 'Yes' : 'No'}
          onChange={(e) =>
            onFieldChange('isBillable', e.target.value === 'Yes')
          }
        >
          <FormControlLabel value='Yes' control={<Radio />} label='Yes' />
          <FormControlLabel value='No' control={<Radio />} label='No' />
        </RadioGroup>
      </FormControl>

      {isBillable && (
        <>
          <Grid item xs={12}>
            <CPTCodesDropdownArray
              value={cptCodes || []}
              options={allCptCodes}
              onChange={(selectedValue) =>
                onFieldChange('cptCodes', selectedValue)
              }
              label='CPT Codes'
              message='Select CPT Codes'
              disabled={false}
              subrole={subrole}
              useGroupedDisplay={true}
            />
          </Grid>
          {hasSDPTherapy && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <SDPServiceCodesDropdownArray
                value={sdpServiceCodes || []}
                onChange={(selectedValue) =>
                  onFieldChange('sdpServiceCodes', selectedValue)
                }
                label='Self-Deterministic Program Service Codes'
                message='Select Service Codes'
                disabled={false}
              />
            </Grid>
          )}
          {hasIEPTherapy && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography
                variant='subtitle2'
                sx={{ mb: 1, color: '#9291f0', fontWeight: 600 }}
              >
                IEP Service Therapies
              </Typography>
              <IEPServiceTherapiesField
                studentId={studentId}
                typeOfUser={typeOfUser}
                value={iepServiceTherapies || []}
                onChange={(selectedValue) =>
                  onFieldChange('iepServiceTherapies', selectedValue)
                }
                disabled={false}
              />
            </Grid>
          )}
        </>
      )}

      {!isBillable && (
        <TextField
          fullWidth
          label='Non-Billable Reason / Payer'
          placeholder='Enter username or reason'
          value={nonBillableReason || ''}
          onChange={(e) =>
            onFieldChange('nonBillableReason', e.target.value)
          }
          variant='outlined'
          sx={{ mt: 2 }}
        />
      )}
    </Paper>
  );
};

export default BillingInformationCard;

